import { CheckCircle2, CircleAlert, Loader2, PencilLine } from "lucide-react";

import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

export const DEFERRED_COMMIT_UNSAVED_HINT_SINGLE_LINE =
  "Unsaved — press Enter or leave this field to save";
export const DEFERRED_COMMIT_UNSAVED_HINT_MULTILINE =
  "Unsaved — press Enter to save; Shift+Enter for a new line";
export const DEFERRED_COMMIT_UNSAVED_HINT_DATE =
  "Unsaved — press Enter or leave this field to save";

export function deferredCommitStatusGlyphVisible({
  pending,
  isDirty,
  disabled,
  commitError,
  commitSucceeded,
  showSavedWhenIdle,
}: {
  pending?: boolean;
  isDirty: boolean;
  disabled: boolean;
  commitError?: string | null;
  commitSucceeded?: boolean;
  showSavedWhenIdle?: boolean;
}): boolean {
  if ((commitError?.trim() ?? "") !== "") {
    return true;
  }
  if (pending === true) {
    return true;
  }
  if (commitSucceeded === true && !isDirty) {
    return true;
  }
  if (showSavedWhenIdle === true && !isDirty && !disabled) {
    return true;
  }
  if (isDirty && !disabled) {
    return true;
  }
  return false;
}

/** Dirty, saving, saved, and error glyphs beside deferred-commit fields. */
export function DeferredCommitStatusGlyph({
  pending,
  isDirty,
  disabled,
  commitError,
  commitSucceeded,
  showSavedWhenIdle,
  unsavedHint = DEFERRED_COMMIT_UNSAVED_HINT_SINGLE_LINE,
  savedHint = "Saved",
  className,
}: {
  pending?: boolean;
  isDirty: boolean;
  disabled: boolean;
  commitError?: string | null;
  commitSucceeded?: boolean;
  showSavedWhenIdle?: boolean;
  unsavedHint?: string;
  savedHint?: string;
  className?: string;
}): JSX.Element | null {
  const errorMessage = commitError?.trim() ?? "";
  if (errorMessage !== "") {
    return (
      <HoverHintTrigger
        className={cn("shrink-0", className)}
        trigger={
          <button
            type="button"
            tabIndex={-1}
            className="inline-flex shrink-0 rounded p-0.5 text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1"
            aria-label={errorMessage}
          >
            <CircleAlert className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        }
      >
        <span className="block break-words">{errorMessage}</span>
      </HoverHintTrigger>
    );
  }
  if (pending === true) {
    return (
      <span className={cn("inline-flex shrink-0 text-slate-500", className)} aria-label="Saving">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      </span>
    );
  }
  if ((commitSucceeded === true && !isDirty) || (showSavedWhenIdle === true && !isDirty && !disabled)) {
    return (
      <span className={cn("inline-flex shrink-0 text-emerald-600", className)} aria-label={savedHint}>
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
      </span>
    );
  }
  if (isDirty && !disabled) {
    return (
      <HoverHintTrigger
        className={cn("shrink-0", className)}
        trigger={
          <button
            type="button"
            tabIndex={-1}
            className="inline-flex shrink-0 rounded p-0.5 text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
            aria-label={unsavedHint}
          >
            <PencilLine className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        }
      >
        <span className="block break-words">{unsavedHint}</span>
      </HoverHintTrigger>
    );
  }
  return null;
}
